import {
  ActionIcon,
  Badge,
  Card,
  Group,
  Image,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import type { AttachmentResponse, WarrantyMetadata } from '@markku/types'
import {
  getDaysUntilExpiry,
  isWarrantyExpired,
  isWarrantyExpiringSoon,
} from '@markku/types'
import { formatDate } from '@markku/utils'
import {
  IconShield,
  IconShieldCheck,
  IconShieldX,
  IconTrash,
} from '@tabler/icons-react'

interface WarrantyListProps {
  attachments: AttachmentResponse[]
  onDelete?: (attachmentId: number) => Promise<void>
  canEdit?: boolean
}

export function WarrantyList({
  attachments,
  onDelete,
  canEdit = true,
}: WarrantyListProps) {
  // Filter only warranty attachments
  const warranties = attachments.filter(
    (att) =>
      att.metadata &&
      typeof att.metadata === 'object' &&
      'category' in att.metadata &&
      att.metadata.category === 'warranty',
  )

  if (warranties.length === 0) {
    return (
      <Card withBorder p="xl">
        <Stack align="center" gap="xs">
          <ThemeIcon size={48} radius="xl" color="gray" variant="light">
            <IconShield size={24} />
          </ThemeIcon>
          <Text c="dimmed" size="sm" ta="center">
            Ei tallennettuja takuutodistuksia
          </Text>
        </Stack>
      </Card>
    )
  }

  const handleDelete = (attachmentId: number, deviceName: string) => {
    if (!onDelete) return

    modals.openConfirmModal({
      title: 'Poista takuutodistus',
      centered: true,
      children: (
        <Text>Haluatko varmasti poistaa takuutodistuksen "{deviceName}"?</Text>
      ),
      labels: { confirm: 'Poista', cancel: 'Peruuta' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        await onDelete(attachmentId)
      },
    })
  }

  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
      {warranties.map((warranty) => {
        const metadata = warranty.metadata as WarrantyMetadata
        const expired = isWarrantyExpired(metadata.expiryDate)
        const expiringSoon = isWarrantyExpiringSoon(metadata.expiryDate)
        const daysLeft = getDaysUntilExpiry(metadata.expiryDate)

        const statusColor = expired ? 'red' : expiringSoon ? 'yellow' : 'green'
        const statusIcon = expired ? (
          <IconShieldX size={20} />
        ) : expiringSoon ? (
          <IconShield size={20} />
        ) : (
          <IconShieldCheck size={20} />
        )

        const deviceLabel = metadata.deviceName || 'Nimetön takuu'

        return (
          <Card key={warranty.id} withBorder radius="md" p="sm">
            <Stack gap="sm">
              <Group justify="space-between" align="flex-start">
                <Group gap="sm">
                  <ThemeIcon size="lg" color={statusColor} variant="light">
                    {statusIcon}
                  </ThemeIcon>
                  <Stack gap={0}>
                    <Text fw={600} size="sm" lineClamp={1}>
                      {deviceLabel}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Päättyy {formatDate(metadata.expiryDate)}
                    </Text>
                  </Stack>
                </Group>
                {canEdit && onDelete && (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    onClick={() => handleDelete(warranty.id, deviceLabel)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                )}
              </Group>

              <Badge
                color={statusColor}
                variant="light"
                size="sm"
                fullWidth
                style={{ textAlign: 'center' }}
              >
                {expired
                  ? 'Takuu päättynyt'
                  : expiringSoon
                    ? `${daysLeft} päivää jäljellä`
                    : 'Voimassa'}
              </Badge>

              {metadata.seller && (
                <Text size="xs" c="dimmed">
                  Ostettu: {metadata.seller}
                </Text>
              )}

              {metadata.notes && (
                <Text size="xs" c="dimmed" lineClamp={2}>
                  {metadata.notes}
                </Text>
              )}

              {/* Thumbnail preview */}
              {warranty.mimeType.startsWith('image/') && (
                <Image
                  src={warranty.downloadUrl}
                  alt={deviceLabel}
                  radius="sm"
                  style={{
                    cursor: 'pointer',
                    maxHeight: 120,
                    objectFit: 'cover',
                  }}
                  onClick={() => window.open(warranty.downloadUrl, '_blank')}
                />
              )}

              {warranty.mimeType === 'application/pdf' && (
                <Badge
                  variant="light"
                  color="blue"
                  size="sm"
                  style={{ cursor: 'pointer' }}
                  onClick={() => window.open(warranty.downloadUrl, '_blank')}
                >
                  Avaa PDF
                </Badge>
              )}
            </Stack>
          </Card>
        )
      })}
    </SimpleGrid>
  )
}
